/**
 * useDisclosureLoans Hook
 *
 * Fetches loans with disclosure data and stats
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { disclosureDashboardService } from '@/services/disclosureDashboardService';
import type { DisclosureLoan, DisclosureFilters, DisclosureStats } from '@/types/disclosure-dashboard';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useDisclosureLoans', 'disclosure-dashboard');

interface UseDisclosureLoansOptions {
  filters: DisclosureFilters;
}

interface UseDisclosureLoansResult {
  loans: DisclosureLoan[];
  stats: DisclosureStats;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDisclosureLoans({ filters }: UseDisclosureLoansOptions): UseDisclosureLoansResult {
  const { user } = useAuth();
  const [loans, setLoans] = useState<DisclosureLoan[]>([]);
  const [stats, setStats] = useState<DisclosureStats>({
    totalActiveLoans: 0,
    itopMissing: 0,
    initialPackagesMtd: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user?.organization_id) {
      setLoading(false);
      return;
    }

    const organizationId = user.organization_id;

    setLoading(true);
    setError(null);

    try {
      // Fetch loans and stats in parallel
      const [loansData, statsData] = await Promise.all([
        disclosureDashboardService.getDisclosureLoans(organizationId, filters),
        disclosureDashboardService.getDisclosureStats(organizationId)
      ]);

      setLoans(loansData);
      setStats(statsData);
      log.info('Fetched disclosure data', {
        loansCount: loansData.length,
        stats: statsData
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch disclosures';
      setError(message);
      log.error('Failed to fetch disclosure data', {}, err as Error);
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id, filters]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loans,
    stats,
    loading,
    error,
    refetch: fetchData
  };
}
